import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Get token from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

export interface EmailRecipient {
  address: string;
  name?: string;
}

export interface EmailAttachment {
  name: string;
  content?: string; // Base64 encoded
  blob_url?: string; // Azure blob URL
  /** A task-owned inbound attachment. The backend verifies both values
   * against that task before reading the blob; these are never arbitrary
   * client-supplied storage references. */
  sourceTaskId?: string;
  sourceBlobName?: string;
}

export interface SendEmailRequest {
  to: string | string[];
  cc?: string | string[];
  bcc?: string | string[];
  subject: string;
  body: string;
  isHtml?: boolean;
  attachments?: EmailAttachment[];
  claimId?: string; // To link email to claim
  legacyClaimId?: string; // To link email to legacy claim
  taskId?: string; // Email task ID for reply tracking
  saveToSent?: boolean;
}

export interface EmailMessage {
  id: string;
  subject: string;
  from: {
    emailAddress: {
      address: string;
      name?: string;
    };
  };
  toRecipients: Array<{
    emailAddress: {
      address: string;
      name?: string;
    };
  }>;
  receivedDateTime: string;
  bodyPreview: string;
  hasAttachments: boolean;
  isRead: boolean;
}

export interface EmailSignature {
  outlookSignature?: string;
  customSignature?: string;
  useCustom: boolean;
  activeSignature?: string;
}

export interface InboxResponse {
  success: boolean;
  emails: EmailMessage[];
  total: number;
  hasMore: boolean;
  error?: string;
}

export interface MailFolder {
  id: string;
  displayName: string;
  totalItemCount: number;
  unreadItemCount: number;
}

export interface FoldersResponse {
  success: boolean;
  folders: MailFolder[];
  error?: string;
}


// Send email
export const sendEmail = async (emailData: SendEmailRequest) => {
  try {
    const response = await axios.post(
      `${API_URL}/ui/emails/send`,
      emailData,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Get user's email signature
export const getEmailSignature = async (): Promise<EmailSignature> => {
  try {
    const response = await axios.get<EmailSignature>(
      `${API_URL}/ui/emails/signature`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Save custom email signature
export const saveEmailSignature = async (signature: string, useCustom: boolean = true) => {
  try {
    const response = await axios.post(
      `${API_URL}/ui/emails/signature`,
      { signature, useCustom },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Get inbox emails
export const getInboxEmails = async (
  folder: string = 'inbox',
  skip: number = 0,
  top: number = 50,
  search?: string,
  targetEmail?: string
): Promise<InboxResponse> => {
  try {
    const params: any = { folder, skip, top };
    if (search) params.search = search;
    if (targetEmail) params.targetEmail = targetEmail;
    
    const response = await axios.get<InboxResponse>(
      `${API_URL}/ui/emails/inbox`,
      { 
        headers: getAuthHeaders(),
        params
      }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Get email details
export const getEmailDetails = async (messageId: string, targetEmail?: string) => {
  try {
    const params: any = {};
    if (targetEmail) params.targetEmail = targetEmail;
    
    const response = await axios.get(
      `${API_URL}/ui/emails/message/${messageId}`,
      { 
        headers: getAuthHeaders(),
        params
      }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Mark email as read
export const markEmailAsRead = async (messageId: string) => {
  try {
    const response = await axios.post(
      `${API_URL}/ui/emails/message/${messageId}/read`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Assign email to claim or legacy claim
export const assignEmailToClaim = async (messageId: string, claimId?: string, legacyClaimId?: string) => {
  try {
    const payload: any = { messageId };
    if (claimId) {
      payload.claimId = claimId;
    } else if (legacyClaimId) {
      payload.legacyClaimId = legacyClaimId;
    }
    
    const response = await axios.post(
      `${API_URL}/ui/emails/assign-to-claim`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Download email attachment
export const downloadEmailAttachment = async (messageId: string, attachmentId: string, attachmentName: string, targetEmail?: string) => {
  try {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (targetEmail) params.append('targetEmail', targetEmail);
    
    const apiUrl = `${API_URL}/ui/emails/message/${messageId}/attachment/${attachmentId}${params.toString() ? '?' + params.toString() : ''}`;
    
    const response = await fetch(apiUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to download attachment');
    }
    
    // Get the blob from the response
    const blob = await response.blob();
    
    // Create a temporary URL for the blob
    const blobUrl = window.URL.createObjectURL(blob);
    
    // Create a temporary anchor element and trigger download
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = attachmentName;
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    window.URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);
    
    return { success: true };
  } catch (error: any) {
    console.error('Error downloading attachment:', error);
    throw error;
  }
};

// Get mail folders
export const getMailFolders = async (targetEmail?: string): Promise<FoldersResponse> => {
  try {
    const params: any = {};
    if (targetEmail) params.targetEmail = targetEmail;
    
    const response = await axios.get<FoldersResponse>(
      `${API_URL}/ui/emails/folders`,
      { 
        headers: getAuthHeaders(),
        params
      }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};
